import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/state/authStore';
import { api } from '@/api/client';

const MENU_ITEMS: Array<{
  label: string;
  to: string;
  show: (user: { is_feedbacker: boolean; is_admin: boolean }) => boolean;
  badgeKey?: string;
}> = [
  { label: 'クライアント一覧', to: '/clients', show: () => true },
  { label: 'セッション一覧', to: '/sessions', show: () => true, badgeKey: 'sessions' },
  { label: 'ユーザー設定', to: '/settings', show: () => true },
  {
    label: 'タスクダッシュボード',
    to: '/dashboard',
    show: (u) => u.is_feedbacker || u.is_admin,
    badgeKey: 'dashboard',
  },
  {
    label: 'ユーザー管理',
    to: '/admin/coaches',
    show: (u) => u.is_admin,
  },
];

export function AppSidebar() {
  const user = useAuthStore((s) => s.currentUser);
  const location = useLocation();
  const [hasRequiredTasks, setHasRequiredTasks] = useState(false);
  const [hasActionSessions, setHasActionSessions] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const refreshBadges = () => {
      // セッション一覧から requires_my_action を確認
      api.get('/sessions')
        .then(r => {
          if (cancelled) return;
          const sessions = r.data as Array<{ requires_my_action: boolean }>;
          setHasActionSessions(sessions.some(s => s.requires_my_action));
        })
        .catch(() => {
          if (!cancelled) setHasActionSessions(false);
        });

      // ダッシュボード（feedbacker/admin のみ）
      if (user.is_feedbacker || user.is_admin) {
        api.get('/dashboard/tasks?scope=mine')
          .then(r => {
            if (!cancelled) {
              setHasRequiredTasks(
                r.data.required?.length > 0 || r.data.overall_analysis_unread?.length > 0,
              );
            }
          })
          .catch(() => {
            if (!cancelled) setHasRequiredTasks(false);
          });
      } else {
        setHasRequiredTasks(false);
      }
    };

    refreshBadges();
    window.addEventListener('focus', refreshBadges);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshBadges);
    };
  }, [user, location.pathname]);

  if (!user) return null;

  return (
    <aside className="w-60 border-r border-gray-200 bg-white">
      <div className="flex h-14 items-center border-b border-gray-200 px-4">
        <span className="text-sm font-semibold text-gray-500">メニュー</span>
      </div>
      <nav className="flex flex-col gap-1 p-3">
        {MENU_ITEMS.filter((item) => item.show(user)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                'flex items-center justify-between rounded px-3 py-2 text-sm',
                isActive
                  ? 'bg-blue-50 font-medium text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50',
              ].join(' ')
            }
          >
            {item.label}
            {item.badgeKey === 'sessions' && hasActionSessions && (
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
            )}
            {item.badgeKey === 'dashboard' && hasRequiredTasks && (
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
